import * as XLSX from 'xlsx';

const currencies = [
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham (Dubai)' },
    { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
    { code: 'QAR', symbol: '﷼', name: 'Qatari Riyal' },
    { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar' },
    { code: 'BHD', symbol: '.د.ب', name: 'Bahraini Dinar' },
    { code: 'OMR', symbol: '﷼', name: 'Omani Rial' }
];

const apiBase = 'https://api.processoptimizer.ai/api';
// const apiBase = 'http://localhost:8001/api';
// const isProduction = process.env.NODE_ENV === 'production';
// const apiBase = isProduction ? 'https://api.processoptimizer.ai/api' : 'http://localhost:8001/api';


class ModelSettingsAPI {
  static async getModelSettings(userId) {
    try {
      const response = await fetch(`${apiBase}/modelSettings/${userId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch model settings');
      }
      
      return data.data;
    } catch (error) {
      console.error('Error fetching model settings:', error);
      throw error;
    }
  }

  static async updateModelSettings(userId, settings) {
    try {
      const response = await fetch(`${apiBase}/modelSettings/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to update model settings');
      }
      
      return data.data;
    } catch (error) {
      console.error('Error updating model settings:', error);
      throw error;
    }
  }

  static async getAvailableOptions() {
    try {
      const response = await fetch(`${apiBase}/modelSettings/options/available`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch available options');
      }
      
      return data.data;
    } catch (error) {
      console.error('Error fetching available options:', error);
      throw error;
    }
  }
}

// Generic API request function
export const apiRequest = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${apiBase}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// Helper function to handle API responses
const handleResponse = async (response) => {
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const result = await response.json();
    if (!result.success) {
        throw new Error(result.message || "API request failed");
    }
    return result;
};

// Users API 
export const usersAPI = {
  fetchAll: async () => {
    const response = await fetch(`${apiBase}/users/getAllUsers`);
    const data = await response.json();
    
    if (response.ok && data.success) {
      return data.users.map(user => ({
        id: user._id,
        username: user.userName,
        role: user.role
      }));
    } else {
      throw new Error(data.message || 'Failed to fetch users');
    }
  },

  // Create new user
  create: async (userData) => {
    const response = await fetch(`${apiBase}/users/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userName: userData.username,
        password: userData.password,
        role: userData.role
      }),
    });

    const data = await response.json();
    if (response.ok) {
      return data;
    } else {
      throw new Error(data.message || 'Failed to create user');
    }
  },

  // Delete user
  delete: async (userId) => {
    const response = await fetch(`${apiBase}/users/delete/${userId}`, {
      method: 'DELETE',
    });

    const data = await response.json();
    if (response.ok) {
      return data;
    } else {
      throw new Error(data.message || 'Failed to delete user');
    }
  },

  // Change password
  changePassword: async (adminName, userName, newPassword) => {
    const response = await fetch(`${apiBase}/users/change-password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        adminName,
        userName,
        newPassword
      }),
    });

    const data = await response.json();
    if (response.ok) {
      return data;
    } else {
      throw new Error(data.message || 'Failed to change password');
    }
  }
};

// Global Properties API
export const globalPropertiesAPI = {
  fetchAll: () => apiRequest('/globalProperties'),
  create: (propertyData) => apiRequest('/globalProperties/create', {
    method: 'POST',
    body: JSON.stringify(propertyData),
  }),
  update: (propertyId, propertyData) => apiRequest(`/globalProperties/${propertyId}`, {
    method: 'PUT',
    body: JSON.stringify(propertyData),
  }),
  delete: (propertyId) => apiRequest(`/globalProperties/${propertyId}`, {
    method: 'DELETE',
  }),
};

// Property Groups API
export const propertyGroupsAPI = {
  fetchAll: () => apiRequest('/propertyGroups'),
  create: (groupData) => apiRequest('/propertyGroups/create', {
    method: 'POST',
    body: JSON.stringify(groupData),
  }),
  update: (groupId, groupData) => apiRequest(`/propertyGroups/${groupId}`, {
    method: 'PUT',
    body: JSON.stringify(groupData),
  }),
  delete: (groupId) => apiRequest(`/propertyGroups/${groupId}`, {
    method: 'DELETE',
  }),
};

// Property Sets API
export const propertySetsAPI = {
  fetchAll: () => apiRequest('/propertySets'),
  create: (setData) => apiRequest('/propertySets/create', {
    method: 'POST',
    body: JSON.stringify(setData),
  }),
  update: (setId, setData) => apiRequest(`/propertySets/${setId}`, {
    method: 'PUT',
    body: JSON.stringify(setData),
  }),
  delete: (setId) => apiRequest(`/propertySets/${setId}`, {
    method: 'DELETE',
  }),
};

// Reports API
export const reportsAPI = {
  fetchAllProperties: () => apiRequest('/allPropertiesReport'),
};

// API Service functions
export const apiService = {
    optimizedResults: {
        getResults: (params = {}) => {
            const queryString = new URLSearchParams(params).toString();
            return fetch(`${apiBase}/optimizedResults/getOptimizedResults?${queryString}`).then(res => res.json());
        },
        delete: (id) => fetch(`${apiBase}/optimizedResults/deleteOptimizedResult/${id}`, { 
            method: 'DELETE' 
        }).then(res => res.json()),
        deleteAll: () => fetch(`${apiBase}/optimizedResults/deleteAllOptimizedResults`, { 
            method: 'DELETE' 
        }).then(res => res.json())
    },

    components: {
        getAll: () => fetch(`${apiBase}/components`).then(res => res.json()),
        delete: (id) => fetch(`${apiBase}/components/${id}`, { method: 'DELETE' }).then(res => res.json()),
        deleteAll: () => fetch(`${apiBase}/components/deleteAll`, { method: 'DELETE' }).then(res => res.json())
    },

    gradeProperties: {
        getList: (params = {}) => {
            const queryString = new URLSearchParams(params).toString();
            return fetch(`${apiBase}/gradeProperties/getList?${queryString}`).then(res => res.json());
        },
        delete: (id) => fetch(`${apiBase}/gradeProperties/removeGradeProperties/${id}`, { method: 'DELETE' }).then(res => res.json()),
        deleteAll: () => fetch(`${apiBase}/gradeProperties/deleteAll`, { method: 'DELETE' }).then(res => res.json())
    }
};




export const componentServices = {
    fetchComponents: async () => {
        const response = await fetch(`${apiBase}/components`);
        const result = await handleResponse(response);
        
        return result.data.map(component => ({
            _id: component._id,
            name: component.name,
            cost: component.cost,
            costUnit: component.costUnit,
            gravity: component.gravity, 
            minVolume: component.minVolume,
            maxVolume: component.maxVolume,
            availableVolume: component.availableVolume,
            volumeUnit: component.volumeUnit,
            density: component.density,
            selectedProperties: component.selectedProperties || [],
            selectedGroups: component.selectedGroups || [],
            selectedProperty: component.selectedProperty || '',
            source: component.source || 'api',
            sourceDisplay: component.source === 'excel-imported' ? 'Excel Imported' : 'API Component',
            createdAt: component.createdAt,
            timestamp: component.createdAt
        }));
    },

    // Fetch added components
    fetchAddedComponents: async () => {
        const response = await fetch(`${apiBase}/added-components`);
        const result = await handleResponse(response);
        return result.data || [];
    },

    // Save component to MongoDB
    saveComponent: async (componentData) => {
        const mongoComponentData = {
            name: componentData.name,
            cost: componentData.cost ? Number(componentData.cost) : 0,
            costUnit: componentData.costUnit,
            gravity: componentData.gravity ? Number(componentData.gravity) : 0, 
            minVolume: componentData.minVolume ? Number(componentData.minVolume) : 0,
            maxVolume: componentData.maxVolume ? Number(componentData.maxVolume) : 1000,
            availableVolume: componentData.availableVolume ? Number(componentData.availableVolume) : 500,
            volumeUnit: componentData.volumeUnit,
            density: componentData.density ? Number(componentData.density) : 0,
            selectedProperties: componentData.selectedProperties || [],
            selectedGroups: componentData.selectedGroups || [],
            selectedProperty: componentData.selectedProperty || "",
            source: componentData.source || "manual"
        };

        const response = await fetch(`${apiBase}/components`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(mongoComponentData),
        });

        return handleResponse(response);
    },

    // Update component
    updateComponent: async (id, componentData) => {
        const mongoComponentData = {
            name: componentData.name,
            cost: componentData.cost ? Number(componentData.cost) : 0,
            costUnit: componentData.costUnit,
            gravity: componentData.gravity ? Number(componentData.gravity) : 0, 
            minVolume: componentData.minVolume ? Number(componentData.minVolume) : 0,
            maxVolume: componentData.maxVolume ? Number(componentData.maxVolume) : 1000,
            availableVolume: componentData.availableVolume ? Number(componentData.availableVolume) : 500,
            volumeUnit: componentData.volumeUnit,
            density: componentData.density ? Number(componentData.density) : 0,
            selectedProperties: componentData.selectedProperties || [],
            selectedGroups: componentData.selectedGroups || [],
            selectedProperty: componentData.selectedProperty || ""
        };

        const response = await fetch(`${apiBase}/components/${id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(mongoComponentData),
        });

        return handleResponse(response);
    },

    // Delete component
    deleteComponent: async (id) => {
        const response = await fetch(`${apiBase}/components/${id}`, {
            method: "DELETE",
        });

        return handleResponse(response);
    },

    // Create added component
    createAddedComponent: async (componentData) => {
        const mongoComponentData = {
            name: componentData.name,
            cost: componentData.cost ? Number(componentData.cost) : 0,
            costUnit: componentData.costUnit,
            gravity: componentData.gravity ? Number(componentData.gravity) : 0, 
            minVolume: componentData.minVolume ? Number(componentData.minVolume) : 0,
            maxVolume: componentData.maxVolume ? Number(componentData.maxVolume) : 1000,
            availableVolume: componentData.availableVolume ? Number(componentData.availableVolume) : 500,
            volumeUnit: componentData.volumeUnit,
            density: componentData.density ? Number(componentData.density) : 0,
            selectedProperties: componentData.selectedProperties || [],
            selectedGroups: componentData.selectedGroups || [],
            selectedProperty: componentData.selectedProperty || "",
            source: componentData.source || "manual"
        };

        const response = await fetch(`${apiBase}/added-components`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(mongoComponentData),
        });

        return handleResponse(response);
    },

    // Update added component
    updateAddedComponent: async (id, componentData) => {
        const mongoComponentData = {
            name: componentData.name,
            cost: componentData.cost ? Number(componentData.cost) : 0,
            costUnit: componentData.costUnit,
            gravity: componentData.gravity ? Number(componentData.gravity) : 0, 
            minVolume: componentData.minVolume ? Number(componentData.minVolume) : 0,
            maxVolume: componentData.maxVolume ? Number(componentData.maxVolume) : 1000,
            availableVolume: componentData.availableVolume ? Number(componentData.availableVolume) : 500,
            volumeUnit: componentData.volumeUnit,
            density: componentData.density ? Number(componentData.density) : 0,
            selectedProperties: componentData.selectedProperties || [],
            selectedGroups: componentData.selectedGroups || [],
            selectedProperty: componentData.selectedProperty || ""
        };

        const response = await fetch(`${apiBase}/components/${id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(mongoComponentData),
        });

        return handleResponse(response);
    },

    // Delete added component
    deleteAddedComponent: async (id) => {
        const response = await fetch(`${apiBase}/components/${id}`, {
            method: "DELETE",
        });

        return handleResponse(response);
    }
};



// Group Reports API
export const groupReportServices = {
    fetchGroupReports: async () => {
        const response = await fetch(`${apiBase}/group-reports`);
        const result = await handleResponse(response);
        return result.data;
    },

    // Create group report
    createGroupReport: async (groupData) => {
        const response = await fetch(`${apiBase}/group-reports`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(groupData),
        });

        return handleResponse(response);
    },

    // Update group report
    updateGroupReport: async (id, groupData) => {
        const response = await fetch(`${apiBase}/group-reports/${id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(groupData),
        });

        return handleResponse(response);
    },

    // Delete group report
    deleteGroupReport: async (id) => {
        const response = await fetch(`${apiBase}/group-reports/${id}`, {
            method: "DELETE",
        });

        return handleResponse(response);
    }
};

// Properties API
export const propertyServices = {
    fetchGlobalProperties: async () => {
        const response = await fetch(`${apiBase}/globalProperties`);
        const result = await handleResponse(response);
        return result.data || [];
    },

    // Fetch property groups
    fetchPropertyGroups: async () => {
        const response = await fetch(`${apiBase}/propertyGroups`);
        const result = await handleResponse(response);
        return result.data || [];
    },

    // Fetch property sets
    fetchPropertySets: async () => {
        const response = await fetch(`${apiBase}/propertySets`);
        const result = await handleResponse(response);
        return result.data || [];
    },

    // Fetch all properties
    fetchAllProperties: async () => {
        try {
            const [globalProperties, propertyGroups, propertySets] = await Promise.all([
                propertyServices.fetchGlobalProperties(),
                propertyServices.fetchPropertyGroups(),
                propertyServices.fetchPropertySets()
            ]);

            return {
                globalProperties,
                propertyGroups,
                propertySets
            };
        } catch (error) {
            console.error("Error fetching properties:", error);
            return {
                globalProperties: [],
                propertyGroups: [],
                propertySets: []
            };
        }
    }
};



// Excel Import Services
// export const excelImportServices = {
//     processExcelForPreview: (file) => {
//         return new Promise((resolve, reject) => {
//             const reader = new FileReader();

//             reader.onload = (e) => {
//                 try {
//                     const data = new Uint8Array(e.target.result);
//                     const workbook = XLSX.read(data, { type: 'array' });
//                     const sheetName = workbook.SheetNames[0];
//                     const worksheet = workbook.Sheets[sheetName];
//                     const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

//                     if (jsonData.length === 0) {
//                         reject(new Error("Excel file is empty"));
//                         return;
//                     }

//                     const headers = jsonData[0];
                    
//                     const propertyColumns = [];
//                     const excludedColumns = [
//                         'name', 'component name', 'component', 'cost', 'price',
//                         'cost unit', 'currency', 'gravity', 'min volume', 'minimum volume',
//                         'max volume', 'maximum volume', 'available volume', 'volume',
//                         'volume unit', 'density'
//                     ];
                    
//                     headers.forEach((header, index) => {
//                         if (header) {
//                             const headerLower = header.toString().toLowerCase().trim();
//                             const isExcluded = excludedColumns.some(excluded => 
//                                 headerLower.includes(excluded)
//                             );
                            
//                             if (!isExcluded) {
//                                 propertyColumns.push({
//                                     index: index,
//                                     name: header.toString().trim(),
//                                     originalName: header.toString().trim()
//                                 });
//                             }
//                         }
//                     });

//                     const previewComponents = [];
                    
//                     for (let i = 1; i < jsonData.length; i++) {
//                         const row = jsonData[i];
//                         if (!row || row.length === 0) continue;

//                         let name = '';
//                         for (let j = 0; j < headers.length; j++) {
//                             const header = headers[j];
//                             if (header && row[j] !== undefined) {
//                                 const headerLower = header.toString().toLowerCase().trim();
//                                 if (headerLower.includes('name') || headerLower.includes('component')) {
//                                     name = row[j]?.toString().trim() || `Component_${i}`;
//                                     break;
//                                 }
//                             }
//                         }
                        
//                         if (!name) {
//                             for (let j = 0; j < row.length; j++) {
//                                 if (row[j] !== undefined && row[j] !== null && row[j] !== '') {
//                                     name = row[j]?.toString().trim() || `Component_${i}`;
//                                     break;
//                                 }
//                             }
//                         }

//                         let cost = 0;
//                         let costUnit = 'INR';
                        
//                         for (let j = 0; j < headers.length; j++) {
//                             const header = headers[j];
//                             if (header && row[j] !== undefined) {
//                                 const headerLower = header.toString().toLowerCase().trim();
//                                 if (headerLower.includes('cost') || headerLower.includes('price')) {
//                                     const value = row[j];
//                                     if (value !== null && value !== undefined && value !== '') {
//                                         if (typeof value === 'string') {
//                                             cost = parseFloat(value.replace(/[^\d.-]/g, '')) || 0;
//                                         } else {
//                                             cost = Number(value) || 0;
//                                         }
//                                     }
                                    
//                                     if (j + 1 < headers.length) {
//                                         const nextHeader = headers[j + 1]?.toString().toLowerCase().trim();
//                                         if (nextHeader && (nextHeader.includes('unit') || nextHeader.includes('currency'))) {
//                                             const unitValue = row[j + 1];
//                                             if (unitValue) {
//                                                 costUnit = unitValue.toString().trim();
//                                             }
//                                         }
//                                     }
//                                     break;
//                                 }
//                             }
//                         }

//                         let gravity = 0;
//                         for (let j = 0; j < headers.length; j++) {
//                             const header = headers[j];
//                             if (header && row[j] !== undefined) {
//                                 const headerLower = header.toString().toLowerCase().trim();
//                                 if (headerLower.includes('gravity')) {
//                                     const value = row[j];
//                                     if (value !== null && value !== undefined && value !== '') {
//                                         gravity = Number(value) || 0;
//                                     }
//                                     break;
//                                 }
//                             }
//                         }

//                         let minVolume = 0;
//                         let maxVolume = 1000;
//                         let availableVolume = 500;
//                         let volumeUnit = 'L';
//                         let density = 0.85;

//                         for (let j = 0; j < headers.length; j++) {
//                             const header = headers[j];
//                             if (header && row[j] !== undefined) {
//                                 const headerLower = header.toString().toLowerCase().trim();
//                                 const value = row[j];
                                
//                                 if (headerLower.includes('min volume') || headerLower.includes('minimum')) {
//                                     if (value !== null && value !== undefined && value !== '') {
//                                         minVolume = Number(value) || 0;
//                                     }
//                                 } else if (headerLower.includes('max volume') || headerLower.includes('maximum')) {
//                                     if (value !== null && value !== undefined && value !== '') {
//                                         maxVolume = Number(value) || 1000;
//                                     }
//                                 } else if (headerLower.includes('available volume') || headerLower.includes('volume')) {
//                                     if (value !== null && value !== undefined && value !== '') {
//                                         availableVolume = Number(value) || 500;
//                                     }
                                    
//                                     if (j + 1 < headers.length) {
//                                         const nextHeader = headers[j + 1]?.toString().toLowerCase().trim();
//                                         if (nextHeader && nextHeader.includes('unit')) {
//                                             const unitValue = row[j + 1];
//                                             if (unitValue) {
//                                                 volumeUnit = unitValue.toString().trim();
//                                             }
//                                         }
//                                     }
//                                 } else if (headerLower.includes('density')) {
//                                     if (value !== null && value !== undefined && value !== '') {
//                                         density = Number(value) || 0.85;
//                                     }
//                                 }
//                             }
//                         }

//                         const selectedProperties = [];
                        
//                         propertyColumns.forEach(prop => {
//                             const value = row[prop.index];
//                             if (value !== undefined && value !== null && value !== '') {
//                                 const numValue = typeof value === 'string' 
//                                     ? parseFloat(value.replace(/[^\d.-]/g, '')) 
//                                     : Number(value);
                                
//                                 if (!isNaN(numValue)) {
//                                     selectedProperties.push({
//                                         name: prop.name,
//                                         value: numValue,
//                                         uom: '', 
//                                         minValue: 0,
//                                         maxValue: 100,
//                                         defaultValue: numValue
//                                     });
//                                 }
//                             }
//                         });

//                         const component = {
//                             id: `preview-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`,
//                             name: name,
//                             cost: cost,
//                             costUnit: costUnit,
//                             gravity: gravity,
//                             minVolume: minVolume,
//                             maxVolume: maxVolume,
//                             availableVolume: availableVolume,
//                             volumeUnit: volumeUnit,
//                             density: density,
//                             selectedProperties: selectedProperties,
//                             selectedGroups: [],
//                             source: "excel-imported",
//                             originalRow: row,
//                             originalHeaders: headers
//                         };

//                         previewComponents.push(component);
//                     }

//                     resolve(previewComponents);
//                 } catch (error) {
//                     console.error("Excel processing error:", error);
//                     reject(error);
//                 }
//             };

//             reader.onerror = () => {
//                 reject(new Error("Failed to read file"));
//             };

//             reader.readAsArrayBuffer(file);
//         });
//     },

//     // Import components from preview data
//     importComponents: async (selectedComponents) => {
//         const importedComponents = [];
//         let importedCount = 0;
//         let errorCount = 0;

//         for (const component of selectedComponents) {
//             try {
//                 const componentData = {
//                     name: component.name,
//                     cost: component.cost,
//                     costUnit: component.costUnit,
//                     gravity: component.gravity || 0,
//                     minVolume: component.minVolume || 0,
//                     maxVolume: component.maxVolume || 1000,
//                     availableVolume: component.availableVolume || 500,
//                     volumeUnit: component.volumeUnit || 'L',
//                     density: component.density || 0.85,
//                     selectedProperties: component.selectedProperties || [],
//                     selectedGroups: component.selectedGroups || [],
//                     selectedProperty: component.selectedProperty || '',
//                     source: "excel-imported"
//                 };

//                 // Save to main components collection
//                 const result = await componentServices.saveComponent(componentData);

//                 if (result.success) {
//                     importedComponents.push({
//                         ...componentData,
//                         _id: result.data?._id,
//                         id: result.data?._id,
//                         createdAt: new Date().toISOString(),
//                         sourceDisplay: 'Excel Imported'
//                     });
//                     importedCount++;
//                 }
//             } catch (error) {
//                 console.error(`Failed to import component ${component.name}:`, error);
//                 errorCount++;
//             }
//         }

//         return {
//             importedComponents,
//             importedCount,
//             errorCount
//         };
//     },

//     // New function for direct import
//     importComponentsDirect: async (file) => {
//         return new Promise((resolve, reject) => {
//             const reader = new FileReader();

//             reader.onload = async (e) => {
//                 try {
//                     const data = new Uint8Array(e.target.result);
//                     const workbook = XLSX.read(data, { type: 'array' });
//                     const sheetName = workbook.SheetNames[0];
//                     const worksheet = workbook.Sheets[sheetName];
//                     const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

//                     if (jsonData.length === 0) {
//                         reject(new Error("Excel file is empty"));
//                         return;
//                     }

//                     const headers = jsonData[0];
//                     const importedComponents = [];
//                     let successCount = 0;
//                     let errorCount = 0;

//                     // Process each row
//                     for (let i = 1; i < jsonData.length; i++) {
//                         try {
//                             const row = jsonData[i];
//                             if (!row || row.length === 0) continue;

//                             // Extract data using the same logic as preview
//                             const componentData = extractComponentFromRow(headers, row, i);
                            
//                             // Save to database
//                             const result = await componentServices.saveComponent(componentData);
                            
//                             if (result.success) {
//                                 successCount++;
//                                 importedComponents.push({
//                                     ...componentData,
//                                     _id: result.data?._id,
//                                     id: result.data?._id,
//                                     createdAt: new Date().toISOString()
//                                 });
//                             }
//                         } catch (rowError) {
//                             console.error(`Error in row ${i}:`, rowError);
//                             errorCount++;
//                         }
//                     }

//                     resolve({
//                         success: true,
//                         importedComponents,
//                         importedCount: successCount,
//                         errorCount
//                     });
//                 } catch (error) {
//                     reject(error);
//                 }
//             };

//             reader.onerror = () => {
//                 reject(new Error("Failed to read file"));
//             };

//             reader.readAsArrayBuffer(file);
//         });
//     }
// };


export const excelImportServices = {
    processExcelForPreview: (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                    if (jsonData.length === 0) {
                        reject(new Error("Excel file is empty"));
                        return;
                    }

                    const headers = jsonData[0];
                    
                    const propertyColumns = [];
                    const excludedColumns = [
                        'name', 'component name', 'component', 'cost', 'price',
                        'cost unit', 'currency', 'gravity', 'min', 'minimum',
                        'max', 'maximum', 'available', 'availability', 'volume',
                        'volume unit', 'density', 'min volume', 'minimum volume',
                        'max volume', 'maximum volume', 'available volume'
                    ];
                    
                    headers.forEach((header, index) => {
                        if (header) {
                            const headerLower = header.toString().toLowerCase().trim();
                            const isExcluded = excludedColumns.some(excluded => 
                                headerLower.includes(excluded)
                            );
                            
                            if (!isExcluded) {
                                propertyColumns.push({
                                    index: index,
                                    name: header.toString().trim(),
                                    originalName: header.toString().trim()
                                });
                            }
                        }
                    });

                    const previewComponents = [];
                    
                    for (let i = 1; i < jsonData.length; i++) {
                        const row = jsonData[i];
                        if (!row || row.length === 0) continue;

                        let name = '';
                        for (let j = 0; j < headers.length; j++) {
                            const header = headers[j];
                            if (header && row[j] !== undefined) {
                                const headerLower = header.toString().toLowerCase().trim();
                                if (headerLower.includes('name') || headerLower.includes('component')) {
                                    name = row[j]?.toString().trim() || `Component_${i}`;
                                    break;
                                }
                            }
                        }
                        
                        if (!name) {
                            for (let j = 0; j < row.length; j++) {
                                if (row[j] !== undefined && row[j] !== null && row[j] !== '') {
                                    name = row[j]?.toString().trim() || `Component_${i}`;
                                    break;
                                }
                            }
                        }

                        let cost = 0;
                        let costUnit = 'INR';
                        
                        for (let j = 0; j < headers.length; j++) {
                            const header = headers[j];
                            if (header && row[j] !== undefined) {
                                const headerLower = header.toString().toLowerCase().trim();
                                if (headerLower.includes('cost') || headerLower.includes('price')) {
                                    const value = row[j];
                                    if (value !== null && value !== undefined && value !== '') {
                                        if (typeof value === 'string') {
                                            cost = parseFloat(value.replace(/[^\d.-]/g, '')) || 0;
                                        } else {
                                            cost = Number(value) || 0;
                                        }
                                    }
                                    
                                    if (j + 1 < headers.length) {
                                        const nextHeader = headers[j + 1]?.toString().toLowerCase().trim();
                                        if (nextHeader && (nextHeader.includes('unit') || nextHeader.includes('currency'))) {
                                            const unitValue = row[j + 1];
                                            if (unitValue) {
                                                costUnit = unitValue.toString().trim();
                                            }
                                        }
                                    }
                                    break;
                                }
                            }
                        }

                        let gravity = 0;
                        for (let j = 0; j < headers.length; j++) {
                            const header = headers[j];
                            if (header && row[j] !== undefined) {
                                const headerLower = header.toString().toLowerCase().trim();
                                if (headerLower.includes('gravity')) {
                                    const value = row[j];
                                    if (value !== null && value !== undefined && value !== '') {
                                        gravity = Number(value) || 0;
                                    }
                                    break;
                                }
                            }
                        }

                        let minVolume = 0;
                        let maxVolume = 1000;
                        let availableVolume = 500;
                        let volumeUnit = 'L';
                        let density = 0;

                        for (let j = 0; j < headers.length; j++) {
                            const header = headers[j];
                            if (header && row[j] !== undefined) {
                                const headerLower = header.toString().toLowerCase().trim();
                                const value = row[j];
                                
                                if (headerLower.includes('min') && !headerLower.includes('volume')) {
                                    if (value !== null && value !== undefined && value !== '') {
                                        minVolume = Number(value) || 0;
                                    }
                                } else if (headerLower.includes('max') && !headerLower.includes('volume')) {
                                    if (value !== null && value !== undefined && value !== '') {
                                        maxVolume = Number(value) || 1000;
                                    }
                                } else if ((headerLower.includes('available') || headerLower.includes('availability')) && 
                                         !headerLower.includes('volume')) {
                                    if (value !== null && value !== undefined && value !== '') {
                                        availableVolume = Number(value) || 500;
                                    }
                                } else if (headerLower.includes('min volume') || headerLower.includes('minimum volume')) {
                                    if (value !== null && value !== undefined && value !== '') {
                                        minVolume = Number(value) || 0;
                                    }
                                } else if (headerLower.includes('max volume') || headerLower.includes('maximum volume')) {
                                    if (value !== null && value !== undefined && value !== '') {
                                        maxVolume = Number(value) || 1000;
                                    }
                                } else if (headerLower.includes('available volume')) {
                                    if (value !== null && value !== undefined && value !== '') {
                                        availableVolume = Number(value) || 500;
                                    }
                                    
                                    if (j + 1 < headers.length) {
                                        const nextHeader = headers[j + 1]?.toString().toLowerCase().trim();
                                        if (nextHeader && nextHeader.includes('unit')) {
                                            const unitValue = row[j + 1];
                                            if (unitValue) {
                                                volumeUnit = unitValue.toString().trim();
                                            }
                                        }
                                    }
                                } else if (headerLower.includes('volume') && !headerLower.includes('min') && 
                                         !headerLower.includes('max') && !headerLower.includes('available')) {
                                    if (value !== null && value !== undefined && value !== '') {
                                        availableVolume = Number(value) || 500;
                                    }
                                    
                                    if (j + 1 < headers.length) {
                                        const nextHeader = headers[j + 1]?.toString().toLowerCase().trim();
                                        if (nextHeader && nextHeader.includes('unit')) {
                                            const unitValue = row[j + 1];
                                            if (unitValue) {
                                                volumeUnit = unitValue.toString().trim();
                                            }
                                        }
                                    }
                                } else if (headerLower.includes('density')) {
                                    if (value !== null && value !== undefined && value !== '') {
                                        density = Number(value) || 0;
                                    }
                                }
                            }
                        }

                        const selectedProperties = [];
                        
                        propertyColumns.forEach(prop => {
                            const value = row[prop.index];
                            if (value !== undefined && value !== null && value !== '') {
                                const numValue = typeof value === 'string' 
                                    ? parseFloat(value.replace(/[^\d.-]/g, '')) 
                                    : Number(value);
                                
                                if (!isNaN(numValue)) {
                                    selectedProperties.push({
                                        name: prop.name,
                                        value: numValue,
                                        uom: '', 
                                        minValue: 0,
                                        maxValue: 100,
                                        defaultValue: numValue
                                    });
                                }
                            }
                        });

                        const component = {
                            id: `preview-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`,
                            name: name,
                            cost: cost,
                            costUnit: costUnit,
                            gravity: gravity,
                            minVolume: minVolume,
                            maxVolume: maxVolume,
                            availableVolume: availableVolume,
                            volumeUnit: volumeUnit,
                            density: density,
                            selectedProperties: selectedProperties,
                            selectedGroups: [],
                            source: "excel-imported",
                            originalRow: row,
                            originalHeaders: headers
                        };

                        previewComponents.push(component);
                    }

                    resolve(previewComponents);
                } catch (error) {
                    console.error("Excel processing error:", error);
                    reject(error);
                }
            };

            reader.onerror = () => {
                reject(new Error("Failed to read file"));
            };

            reader.readAsArrayBuffer(file);
        });
    },

    // Import components from preview data
    importComponents: async (selectedComponents) => {
        const importedComponents = [];
        let importedCount = 0;
        let errorCount = 0;

        for (const component of selectedComponents) {
            try {
                // Prepare component data for MongoDB
                const componentData = {
                    name: component.name,
                    cost: component.cost,
                    costUnit: component.costUnit,
                    gravity: component.gravity || 0,
                    minVolume: component.minVolume || 0,
                    maxVolume: component.maxVolume || 1000,
                    availableVolume: component.availableVolume || 500,
                    volumeUnit: component.volumeUnit || 'L',
                    density: component.density !== undefined && component.density !== null && component.density !== '' ? component.density : null,
                    selectedProperties: component.selectedProperties || [],
                    selectedGroups: component.selectedGroups || [],
                    selectedProperty: component.selectedProperty || '',
                    source: "excel-imported"
                };

                // Save to main components collection
                const result = await componentServices.saveComponent(componentData);

                if (result.success) {
                    importedComponents.push({
                        ...componentData,
                        _id: result.data?._id,
                        id: result.data?._id,
                        createdAt: new Date().toISOString(),
                        sourceDisplay: 'Excel Imported'
                    });
                    importedCount++;
                }
            } catch (error) {
                console.error(`Failed to import component ${component.name}:`, error);
                errorCount++;
            }
        }

        return {
            importedComponents,
            importedCount,
            errorCount
        };
    },

    // Helper function for direct import
    extractComponentFromRow: (headers, row, rowIndex) => {
        let name = '';
        for (let j = 0; j < headers.length; j++) {
            const header = headers[j];
            if (header && row[j] !== undefined) {
                const headerLower = header.toString().toLowerCase().trim();
                if (headerLower.includes('name') || headerLower.includes('component')) {
                    name = row[j]?.toString().trim() || `Component_${rowIndex}`;
                    break;
                }
            }
        }
        
        if (!name) {
            for (let j = 0; j < row.length; j++) {
                if (row[j] !== undefined && row[j] !== null && row[j] !== '') {
                    name = row[j]?.toString().trim() || `Component_${rowIndex}`;
                    break;
                }
            }
        }

        let cost = 0;
        let costUnit = 'INR';
        
        for (let j = 0; j < headers.length; j++) {
            const header = headers[j];
            if (header && row[j] !== undefined) {
                const headerLower = header.toString().toLowerCase().trim();
                if (headerLower.includes('cost') || headerLower.includes('price')) {
                    const value = row[j];
                    if (value !== null && value !== undefined && value !== '') {
                        if (typeof value === 'string') {
                            cost = parseFloat(value.replace(/[^\d.-]/g, '')) || 0;
                        } else {
                            cost = Number(value) || 0;
                        }
                    }
                    
                    if (j + 1 < headers.length) {
                        const nextHeader = headers[j + 1]?.toString().toLowerCase().trim();
                        if (nextHeader && (nextHeader.includes('unit') || nextHeader.includes('currency'))) {
                            const unitValue = row[j + 1];
                            if (unitValue) {
                                costUnit = unitValue.toString().trim();
                            }
                        }
                    }
                    break;
                }
            }
        }

        let gravity = 0;
        for (let j = 0; j < headers.length; j++) {
            const header = headers[j];
            if (header && row[j] !== undefined) {
                const headerLower = header.toString().toLowerCase().trim();
                if (headerLower.includes('gravity')) {
                    const value = row[j];
                    if (value !== null && value !== undefined && value !== '') {
                        gravity = Number(value) || 0;
                    }
                    break;
                }
            }
        }

        let minVolume = 0;
        let maxVolume = 1000;
        let availableVolume = 500;
        let volumeUnit = 'L';
        let density = 0;

        for (let j = 0; j < headers.length; j++) {
            const header = headers[j];
            if (header && row[j] !== undefined) {
                const headerLower = header.toString().toLowerCase().trim();
                const value = row[j];
                
                if (headerLower.includes('min') && !headerLower.includes('volume')) {
                    if (value !== null && value !== undefined && value !== '') {
                        minVolume = Number(value) || 0;
                    }
                } else if (headerLower.includes('max') && !headerLower.includes('volume')) {
                    if (value !== null && value !== undefined && value !== '') {
                        maxVolume = Number(value) || 1000;
                    }
                } else if ((headerLower.includes('available') || headerLower.includes('availability')) && 
                         !headerLower.includes('volume')) {
                    if (value !== null && value !== undefined && value !== '') {
                        availableVolume = Number(value) || 500;
                    }
                } else if (headerLower.includes('min volume') || headerLower.includes('minimum volume')) {
                    if (value !== null && value !== undefined && value !== '') {
                        minVolume = Number(value) || 0;
                    }
                } else if (headerLower.includes('max volume') || headerLower.includes('maximum volume')) {
                    if (value !== null && value !== undefined && value !== '') {
                        maxVolume = Number(value) || 1000;
                    }
                } else if (headerLower.includes('available volume')) {
                    if (value !== null && value !== undefined && value !== '') {
                        availableVolume = Number(value) || 500;
                    }
                    
                    if (j + 1 < headers.length) {
                        const nextHeader = headers[j + 1]?.toString().toLowerCase().trim();
                        if (nextHeader && nextHeader.includes('unit')) {
                            const unitValue = row[j + 1];
                            if (unitValue) {
                                volumeUnit = unitValue.toString().trim();
                            }
                        }
                    }
                } else if (headerLower.includes('volume') && !headerLower.includes('min') && 
                         !headerLower.includes('max') && !headerLower.includes('available')) {
                    if (value !== null && value !== undefined && value !== '') {
                        availableVolume = Number(value) || 500;
                    }
                    
                    if (j + 1 < headers.length) {
                        const nextHeader = headers[j + 1]?.toString().toLowerCase().trim();
                        if (nextHeader && nextHeader.includes('unit')) {
                            const unitValue = row[j + 1];
                            if (unitValue) {
                                volumeUnit = unitValue.toString().trim();
                            }
                        }
                    }
                } else if (headerLower.includes('density')) {
                    if (value !== null && value !== undefined && value !== '') {
                        density = Number(value) || 0;
                    }
                }
            }
        }

        const selectedProperties = [];
        const excludedColumns = [
            'name', 'component name', 'component', 'cost', 'price',
            'cost unit', 'currency', 'gravity', 'min', 'minimum',
            'max', 'maximum', 'available', 'availability', 'volume',
            'volume unit', 'density', 'min volume', 'minimum volume',
            'max volume', 'maximum volume', 'available volume'
        ];
        
        headers.forEach((header, index) => {
            if (header) {
                const headerLower = header.toString().toLowerCase().trim();
                const isExcluded = excludedColumns.some(excluded => 
                    headerLower.includes(excluded)
                );
                
                if (!isExcluded) {
                    const value = row[index];
                    if (value !== undefined && value !== null && value !== '') {
                        const numValue = typeof value === 'string' 
                            ? parseFloat(value.replace(/[^\d.-]/g, '')) 
                            : Number(value);
                        
                        if (!isNaN(numValue)) {
                            selectedProperties.push({
                                name: header.toString().trim(),
                                value: numValue,
                                uom: '',
                                minValue: 0,
                                maxValue: 100,
                                defaultValue: numValue
                            });
                        }
                    }
                }
            }
        });

        return {
            name: name,
            cost: cost,
            costUnit: costUnit,
            gravity: gravity,
            minVolume: minVolume,
            maxVolume: maxVolume,
            availableVolume: availableVolume,
            volumeUnit: volumeUnit,
            density: density,
            selectedProperties: selectedProperties,
            selectedGroups: [],
            source: "excel-imported"
        };
    },

    // New function for direct import
    importComponentsDirect: async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                    if (jsonData.length === 0) {
                        reject(new Error("Excel file is empty"));
                        return;
                    }

                    const headers = jsonData[0];
                    const importedComponents = [];
                    let successCount = 0;
                    let errorCount = 0;

                    // Process each row
                    for (let i = 1; i < jsonData.length; i++) {
                        try {
                            const row = jsonData[i];
                            if (!row || row.length === 0) continue;

                            const componentData = excelImportServices.extractComponentFromRow(headers, row, i);
                            
                            const result = await componentServices.saveComponent(componentData);
                            
                            if (result.success) {
                                successCount++;
                                importedComponents.push({
                                    ...componentData,
                                    _id: result.data?._id,
                                    id: result.data?._id,
                                    createdAt: new Date().toISOString()
                                });
                            }
                        } catch (rowError) {
                            console.error(`Error in row ${i}:`, rowError);
                            errorCount++;
                        }
                    }

                    resolve({
                        success: true,
                        importedComponents,
                        importedCount: successCount,
                        errorCount
                    });
                } catch (error) {
                    reject(error);
                }
            };

            reader.onerror = () => {
                reject(new Error("Failed to read file"));
            };

            reader.readAsArrayBuffer(file);
        });
    }
};

function extractComponentFromRow(headers, row, rowIndex) {
    let name = '';
    let cost = 0;
    let costUnit = 'INR';
    let gravity = 0;
    let minVolume = 0;
    let maxVolume = 1000;
    let availableVolume = 500;
    let volumeUnit = 'L';
    let density = 0;
    const selectedProperties = [];

    // Find name
    for (let j = 0; j < headers.length; j++) {
        const header = headers[j];
        if (header && row[j] !== undefined) {
            const headerLower = header.toString().toLowerCase().trim();
            if (headerLower.includes('name') || headerLower.includes('component')) {
                name = row[j]?.toString().trim() || `Component_${rowIndex}`;
                break;
            }
        }
    }
    
    if (!name) {
        for (let j = 0; j < row.length; j++) {
            if (row[j] !== undefined && row[j] !== null && row[j] !== '') {
                name = row[j]?.toString().trim() || `Component_${rowIndex}`;
                break;
            }
        }
    }

    // Extract other fields
    for (let j = 0; j < headers.length; j++) {
        const header = headers[j];
        if (!header || row[j] === undefined) continue;
        
        const headerLower = header.toString().toLowerCase().trim();
        const value = row[j];
        
        // Check for different field types
        if (headerLower.includes('cost') || headerLower.includes('price')) {
            if (value !== null && value !== '' && value !== undefined) {
                if (typeof value === 'string') {
                    cost = parseFloat(value.replace(/[^\d.-]/g, '')) || 0;
                } else {
                    cost = Number(value) || 0;
                }
            }
            
            // Check next column for unit
            if (j + 1 < headers.length) {
                const nextHeader = headers[j + 1]?.toString().toLowerCase().trim();
                if (nextHeader && (nextHeader.includes('unit') || nextHeader.includes('currency'))) {
                    const unitValue = row[j + 1];
                    if (unitValue) {
                        costUnit = unitValue.toString().trim();
                    }
                }
            }
        }
        else if (headerLower.includes('gravity')) {
            if (value !== null && value !== '' && value !== undefined) {
                gravity = Number(value) || 0;
            }
        }
        else if (headerLower.includes('min volume') || headerLower.includes('minimum')) {
            if (value !== null && value !== '' && value !== undefined) {
                minVolume = Number(value) || 0;
            }
        }
        else if (headerLower.includes('max volume') || headerLower.includes('maximum')) {
            if (value !== null && value !== '' && value !== undefined) {
                maxVolume = Number(value) || 1000;
            }
        }
        else if (headerLower.includes('available volume') || headerLower.includes('volume')) {
            if (value !== null && value !== '' && value !== undefined) {
                availableVolume = Number(value) || 500;
            }
            
            // Check next column for unit
            if (j + 1 < headers.length) {
                const nextHeader = headers[j + 1]?.toString().toLowerCase().trim();
                if (nextHeader && nextHeader.includes('unit')) {
                    const unitValue = row[j + 1];
                    if (unitValue) {
                        volumeUnit = unitValue.toString().trim();
                    }
                }
            }
        }
        else if (headerLower.includes('density')) {
            if (value !== null && value !== '' && value !== undefined) {
                density = Number(value) || 0;
            }
        }
        else if (headerLower.includes('unit') && j > 0) {
            // Skip unit columns as they are handled with their values
            continue;
        }
        else {
            // Treat as property
            if (value !== null && value !== '' && value !== undefined) {
                const numValue = typeof value === 'string' 
                    ? parseFloat(value.replace(/[^\d.-]/g, '')) 
                    : Number(value);
                
                if (!isNaN(numValue)) {
                    selectedProperties.push({
                        name: header.toString().trim(),
                        value: numValue,
                        uom: '',
                        minValue: 0,
                        maxValue: 100,
                        defaultValue: numValue
                    });
                }
            }
        }
    }

    return {
        name: name,
        cost: cost,
        costUnit: costUnit,
        gravity: gravity,
        minVolume: minVolume,
        maxVolume: maxVolume,
        availableVolume: availableVolume,
        volumeUnit: volumeUnit,
        density: density,
        selectedProperties: selectedProperties,
        selectedGroups: [],
        selectedProperty: selectedProperties.map(p => p.name).join(', '),
        source: "excel-imported"
    };
}

// Utility functions
export const utils = {
    getCurrencySymbol: (currencyCode, currenciesList = currencies) => {
        const curr = currenciesList.find((c) => c.code === currencyCode);
        return curr ? curr.symbol : "$";
    },

    generateUniqueKey: (component, index, type) => {
        if (component._id) return `${type}-${component._id}`;
        if (component.id) return `${type}-${component.id}`;
        return `${type}-${index}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    },

    getSelectedPropertyNames: (component) => {
        const allSelectedNames = [];

        if (component.selectedProperties && component.selectedProperties.length > 0) {
            component.selectedProperties.forEach(item => {
                allSelectedNames.push(item.name);
            });
        }

        if (component.selectedGroups && component.selectedGroups.length > 0) {
            component.selectedGroups.forEach(item => {
                allSelectedNames.push(`${item.name} (Group)`);
            });
        }

        return allSelectedNames;
    },

    preparePropertyOptions: (globalProperties, propertyGroups) => {
        const globalPropertiesWithUOM = globalProperties.map(prop => ({
            name: prop.property,
            uom: prop.uom,
            displayName: `${prop.property} (${prop.uom})`,
            type: 'property',
            minValue: prop.minValue || 0,
            maxValue: prop.maxValue || 100,
            defaultValue: prop.defaultValue || 50
        }));

        const allProperties = [...globalPropertiesWithUOM].filter((prop, index, self) =>
            index === self.findIndex(p => p.name === prop.name)
        );

        const groupNames = propertyGroups.map(group => group.name).filter(Boolean);
        const allGroupNames = [...new Set([...groupNames])].map(name => ({
            name: name,
            uom: "Group",
            displayName: `${name} (Group)`,
            type: 'group',
            minValue: 0,
            maxValue: 100,
            defaultValue: 50
        }));

        return {
            globalPropertyOptions: allProperties,
            groupPropertyOptions: allGroupNames
        };
    }
};

// Export ModelSettingsAPI as named export
export { ModelSettingsAPI };

export default {
    ModelSettingsAPI,
    apiRequest,
    users: usersAPI,
    globalProperties: globalPropertiesAPI,
    propertyGroups: propertyGroupsAPI,
    propertySets: propertySetsAPI,
    reports: reportsAPI,
    apiService,
    componentServices,
    groupReportServices,
    propertyServices,
    excelImportServices,
    utils,
    currencies,
    apiBase
};